import type { TokenCredential } from "@azure/identity";
import { WORKER_ALIASES } from "@m8t-stack/brain-engine";
import { getAzAccount } from "./auth.js";
import { resolveFoundryProject } from "./foundry-project.js";
import { discoverLedgerResources } from "./dream-discovery.js";

export interface DreamContext {
  worker: string; // canonical, lowercase
  projectEndpoint: string; // Foundry data-plane endpoint (conversation items)
  ledgerTableEndpoint: string; // storage table endpoint (ledger + cursor)
  workspaceId: string; // LA customerId (trace enrich/recovery)
  physicalPks: string[]; // canonical PK + any frozen aliases pointing at it
}

/** Reverse WORKER_ALIASES: canonical → [canonical, ...frozen-alias PKs]. */
function physicalPksFor(canonical: string): string[] {
  const aliases = Object.entries(WORKER_ALIASES)
    .filter(([, c]) => c === canonical)
    .map(([physical]) => physical);
  return [canonical, ...aliases];
}

/** Parse the resource group out of an ARM resource id (…/resourceGroups/<rg>/…). */
function rgFromScope(scope: string): string {
  const m = /\/resourceGroups\/([^/]+)/i.exec(scope);
  if (!m) throw new Error(`Could not parse resource group from '${scope}'.`);
  return m[1];
}

export async function resolveDreamContext(opts: {
  credential: TokenCredential;
  worker: string;
  subscription?: string;
  endpoint?: string;
}): Promise<DreamContext> {
  const { credential } = opts;
  const canonical = opts.worker.toLowerCase();

  const account = await getAzAccount();
  const subscriptionId = opts.subscription ?? account.subscriptionId;

  const project = await resolveFoundryProject({
    credential,
    subscriptionId,
    interactive: false,
    endpoint: opts.endpoint,
  });

  const resourceGroup = rgFromScope(project.accountScope);
  const { ledgerTableEndpoint, workspaceId } = await discoverLedgerResources({
    credential,
    subscriptionId,
    resourceGroup,
  });

  return {
    worker: canonical,
    projectEndpoint: project.endpoint,
    ledgerTableEndpoint,
    workspaceId,
    physicalPks: physicalPksFor(canonical),
  };
}
